// Google Analytics 4 (ADR 0224). Runtime-configured + lazy-loaded so a build with
// GA unset pays NOTHING — exactly like the RUM integrations (ADR 0086):
//
//   • The GA4 measurement id lives on the SAME served runtime config as RUM —
//     `window.__RUM__.ga4Id`, set by the static `/rum.js` (web/public/rum.js) so
//     it's per-environment WITHOUT a rebuild. The committed default is empty, so
//     dev / tests / PR builds send nothing; the per-env deploy writes the real id.
//   • gtag.js is injected only when an id is configured (never fetched otherwise),
//     mirroring rum.ts's dynamic-import gating.
//   • page_view is sent by US on every route change (initial load + SPA nav) with
//     `send_page_view:false` on config, so there is exactly one per route and NO
//     duplicate on first load.
//   • GA4 is ADDITIVE to the first-party analytics beacon (track(), ADR 0017) —
//     the funnel events it fires are forwarded here too; we never rip out the
//     first-party path. No PII is ever sent (ids only, never emails/usernames).
//   • The ADMIN PORTAL (`/admin/*`) is NOT tracked — GA4 is a marketing layer for
//     public/consumer surfaces, so page_views and events are suppressed there
//     (see isAdminPath). The first-party analytics beacon already excludes staff.

import { rumConfig } from "./rum";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __GA_STARTED__?: boolean;
  }
}

/** The configured GA4 measurement id, or "" when unset (GA disabled). */
export function ga4Id(): string {
  try {
    return rumConfig().ga4Id ?? "";
  } catch {
    return "";
  }
}

/** True when GA4 is configured (an id is present). */
export function gaEnabled(): boolean {
  return !!ga4Id();
}

/**
 * True for the admin portal (`/admin` and everything under it — `/admin/:tab`,
 * `/admin/caves/:slug`). GA4 is a marketing-analytics layer for the public /
 * consumer surfaces only; the internal operator admin site is intentionally NOT
 * tracked (no page_views, no events). Pure + exported so the exclusion is
 * unit-testable. Keys on the pathname (query/hash stripped).
 */
export function isAdminPath(path: string): boolean {
  const p = (path || "").split(/[?#]/, 1)[0];
  return p === "/admin" || p.startsWith("/admin/");
}

/** True when the browser is currently on an admin-portal route (best-effort). */
function onAdminSurface(): boolean {
  try {
    if (typeof window === "undefined" || !window.location) return false;
    return isAdminPath(window.location.pathname);
  } catch {
    return false;
  }
}

/**
 * Push a command onto GA's dataLayer. Safe before gtag.js has loaded: the real
 * library replays the queued commands on load, so page_views/events fired during
 * boot are never lost. A no-op (and never throws) when GA is unconfigured.
 */
function gaPush(...args: unknown[]): void {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.gtag !== "function") return; // not started (unconfigured)
    window.gtag(...args);
  } catch {
    /* analytics must never break the app */
  }
}

/**
 * Initialize GA4 if (and only if) a measurement id is configured. Idempotent
 * (guards a double-init) and best-effort — a failure must NEVER break the app.
 * Returns immediately; gtag.js loads asynchronously. `send_page_view:false` is
 * set so WE own page_view (one per route, no load-time duplicate — see
 * {@link gaPageView}).
 */
export function initGa(id: string = ga4Id()): void {
  if (typeof window === "undefined") return;
  if (!id) return; // unset ⇒ no-op (committed default OFF)
  if (window.__GA_STARTED__) return;
  window.__GA_STARTED__ = true;

  try {
    window.dataLayer = window.dataLayer || [];
    // Canonical gtag(): it pushes the `arguments` OBJECT, which is what gtag.js's
    // command processor reads. A plain `dataLayer.push([...])` ARRAY is NOT applied
    // as a gtag command, so GA4 loads (gtag.js 200) but never registers config /
    // sends page_view — the "gtag.js loads but no data in GA" bug (LSC-536). This is
    // the exact Google snippet form, so commands queued before gtag.js loads replay.
    if (typeof window.gtag !== "function") {
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer!.push(arguments);
      };
    }
    gaPush("js", new Date());
    // send_page_view:false — WE send page_view per route (initial + SPA nav).
    // anonymize_ip keeps GA from storing the full client IP.
    gaPush("config", id, { send_page_view: false, anonymize_ip: true });
    // Lazy-load the remote gtag.js only now that an id is configured, so an unset
    // build never fetches it (the rum.ts dynamic-import gating, for a classic
    // <script> SDK). Best-effort — a blocked/failed load just leaves the queue.
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
  } catch {
    /* never let analytics setup break the app */
  }
}

/**
 * Send a GA4 `page_view` for the given SPA path. Called on initial load AND every
 * client navigation (see usePageViews). Sends only the path + title (no PII). A
 * no-op when GA is unconfigured.
 */
export function gaPageView(path: string): void {
  if (!gaEnabled()) return;
  if (isAdminPath(path)) return; // admin portal is not tracked in GA4
  const params: Record<string, unknown> = { page_path: path };
  try {
    if (typeof window !== "undefined" && window.location) {
      params.page_location = window.location.origin + path;
    }
    if (typeof document !== "undefined" && document.title) {
      params.page_title = document.title;
    }
  } catch {
    /* best-effort context only */
  }
  gaPush("event", "page_view", params);
}

/**
 * Send an arbitrary GA4 event. Params must be NON-PII (ids only). A no-op when GA
 * is unconfigured, and never throws.
 */
export function gaEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!gaEnabled()) return;
  if (!name) return;
  if (onAdminSurface()) return; // never emit GA events from the admin portal (covers gaTrack too)
  gaPush("event", name, params);
}

/** Context forwarded from the first-party track() helper (ids only — no PII). */
export interface GaTrackContext {
  caveId?: string;
  legendId?: string;
  cavecastId?: string;
  productId?: string;
  props?: unknown;
}

/**
 * Build the GA4 event (name + non-PII params) that mirrors a first-party
 * analytics event (ADR 0017 ClientEventName). Pure + exported so the mapping is
 * unit-testable without a real gtag. The first-party names are already clean,
 * descriptive, and non-reserved, so they map 1:1; only the context ids are
 * forwarded (never emails/usernames). `props` is forwarded only when it's a flat
 * record of primitives (e.g. { mode: "vod" }); anything else is dropped so we
 * never leak an unexpected object into GA.
 */
export function gaEventForTrack(
  name: string,
  ctx: GaTrackContext = {},
): { event: string; params: Record<string, unknown> } {
  const params: Record<string, unknown> = {};
  if (ctx.caveId) params.cave_id = ctx.caveId;
  if (ctx.legendId) params.legend_id = ctx.legendId;
  if (ctx.cavecastId) params.cavecast_id = ctx.cavecastId;
  if (ctx.productId) params.product_id = ctx.productId;
  if (ctx.props && typeof ctx.props === "object" && !Array.isArray(ctx.props)) {
    for (const [k, v] of Object.entries(ctx.props as Record<string, unknown>)) {
      if (v == null) continue;
      const t = typeof v;
      if (t === "string" || t === "number" || t === "boolean") params[k] = v;
    }
  }
  return { event: name, params };
}

/**
 * Forward a first-party analytics event (track(), ADR 0017) to GA4. GA4 is
 * additive — the first-party beacon still fires independently. A no-op when GA is
 * unconfigured.
 */
export function gaTrack(name: string, ctx: GaTrackContext = {}): void {
  if (!gaEnabled()) return;
  const mapped = gaEventForTrack(name, ctx);
  gaEvent(mapped.event, mapped.params);
}
